/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton } from '@mui/material';
import { isNil } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  selectDataUsageLoading,
  selectReceivedTotalUsageByNetworkError,
  selectReceivedCumulativeUsageByLinkError,
  selectReceivedCumulativeUsageByNetworkError,
  selectReceivedTotalUsageByLinkError,
  selectReceivedUsageByLinkError,
  selectReceivedUsageByNetworkError,
} from '../../store';
import appTheme from '../common/theme/appTheme.json';

interface DataUsageErrorFeedbackProps {
  onRetry: () => void;
}

interface SnackbarActionsProps extends DataUsageErrorFeedbackProps {
  closeSnackbar: () => void;
}

export enum SnackbarKeys {
  ReceivedTotalUsageByNetworkError = "ReceivedTotalUsageByNetworkError",
  ReceivedUsageByNetworkError = "ReceivedUsageByNetworkError",
  ReceivedCumulativeUsageByNetworkError = "ReceivedCumulativeUsageByNetworkError",
  ReceivedTotalUsageByLinkError = "ReceivedTotalUsageByLinkError",
  ReceivedCumulativeUsageByLinkError = "ReceivedCumulativeUsageByLinkError",
  ReceivedUsageByLinkError = "ReceivedUsageByLinkError",
}

// snackbar action buttons
export const SnackbarActions = ({ closeSnackbar, onRetry }: SnackbarActionsProps) => {
  return (
    <>
      <Button sx={{ color: appTheme.primaryTextColor, textTransform: 'none' }} onClick={onRetry}>
        Retry
      </Button>
      <IconButton onClick={closeSnackbar}>
        <CloseIcon sx={{ color: appTheme.primaryTextColor }} />
      </IconButton>
    </>
  );
};

const DataUsageErrorFeedback = ({ onRetry }: DataUsageErrorFeedbackProps) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // selectors
  const isDataUsageLoading = useSelector(selectDataUsageLoading, shallowEqual);

  // Network usage
  const receivedTotalUsageByNetworkError = useSelector(selectReceivedTotalUsageByNetworkError, shallowEqual);
  const receivedUsageByNetworkError = useSelector(selectReceivedUsageByNetworkError, shallowEqual);
  const receivedCumulativeUsageByNetworkError = useSelector(selectReceivedCumulativeUsageByNetworkError, shallowEqual);
 
  // Link usage
  const receivedTotalUsageByLinkError = useSelector(selectReceivedTotalUsageByLinkError, shallowEqual);
  const receivedUsageByLinkError = useSelector(selectReceivedUsageByLinkError, shallowEqual);
  const receivedCumulativeUsageByLinkError = useSelector(selectReceivedCumulativeUsageByLinkError, shallowEqual);

  // display snackbar
  useEffect(() => {
    if (!isNil(receivedTotalUsageByNetworkError)) {
      enqueueSnackbar('Unable to fetch total network usage', {
        key: SnackbarKeys.ReceivedTotalUsageByNetworkError,
        variant: 'error',
        action: (
          <SnackbarActions
            closeSnackbar={() => closeSnackbar(SnackbarKeys.ReceivedTotalUsageByNetworkError)}
            onRetry={onRetry}
          />
        ),
      });
    }
    if (!isNil(receivedUsageByNetworkError)) {
      enqueueSnackbar('Unable to fetch network usage', {
        key: SnackbarKeys.ReceivedUsageByNetworkError,
        variant: 'error',
        action: (
          <SnackbarActions
            closeSnackbar={() => closeSnackbar(SnackbarKeys.ReceivedUsageByNetworkError)}
            onRetry={onRetry}
          />
        ),
      });
    }
    if (!isNil(receivedCumulativeUsageByNetworkError)) {
      enqueueSnackbar('Unable to fetch cumulative network usage', {
        key: SnackbarKeys.ReceivedCumulativeUsageByNetworkError,
        variant: 'error',
        action: (
          <SnackbarActions
            closeSnackbar={() => closeSnackbar(SnackbarKeys.ReceivedCumulativeUsageByNetworkError)}
            onRetry={onRetry}
          />
        ),
      });
    }
    if (!isNil(receivedTotalUsageByLinkError)) {
      enqueueSnackbar('Unable to fetch total link usage', {
        key: SnackbarKeys.ReceivedTotalUsageByLinkError,
        variant: 'error',
        action: (
          <SnackbarActions
            closeSnackbar={() => closeSnackbar(SnackbarKeys.ReceivedTotalUsageByLinkError)}
            onRetry={onRetry}
          />
        ),
      });
    }
    if (!isNil(receivedUsageByLinkError)) {
      enqueueSnackbar('Unable to fetch link usage', {
        key: SnackbarKeys.ReceivedUsageByLinkError,
        variant: 'error',
        action: (
          <SnackbarActions
            closeSnackbar={() => closeSnackbar(SnackbarKeys.ReceivedUsageByLinkError)}
            onRetry={onRetry}
          />
        ),
      });
    }
    if (!isNil(receivedCumulativeUsageByLinkError)) {
      enqueueSnackbar('Unable to fetch cumulative link usage', {
        key: SnackbarKeys.ReceivedCumulativeUsageByLinkError,
        variant: 'error',
        action: (
          <SnackbarActions
            closeSnackbar={() => closeSnackbar(SnackbarKeys.ReceivedCumulativeUsageByLinkError)}
            onRetry={onRetry}
          />
        ),
      });
    }
  }, [
    receivedTotalUsageByNetworkError,
    receivedUsageByNetworkError,
    receivedCumulativeUsageByNetworkError,

    receivedTotalUsageByLinkError,
    receivedUsageByLinkError,
    receivedCumulativeUsageByLinkError,

    isDataUsageLoading,

    onRetry,
    enqueueSnackbar,
    closeSnackbar,
  ]);

  // auto close snackbars if error is resolved
  useEffect(() => {
    if (isNil(receivedTotalUsageByNetworkError) && !isDataUsageLoading) {
      closeSnackbar(SnackbarKeys.ReceivedTotalUsageByNetworkError);
    }
    if (isNil(receivedUsageByNetworkError) && !isDataUsageLoading) {
      closeSnackbar(SnackbarKeys.ReceivedUsageByNetworkError);
    }
    if (isNil(receivedCumulativeUsageByNetworkError) && !isDataUsageLoading) {
      closeSnackbar(SnackbarKeys.ReceivedCumulativeUsageByNetworkError);
    }
    if (isNil(receivedTotalUsageByLinkError) && !isDataUsageLoading) {
      closeSnackbar(SnackbarKeys.ReceivedTotalUsageByLinkError);
    }
    if (isNil(receivedUsageByLinkError) && !isDataUsageLoading) {
      closeSnackbar(SnackbarKeys.ReceivedUsageByLinkError);
    }
    if (isNil(receivedCumulativeUsageByLinkError) && !isDataUsageLoading) {
      closeSnackbar(SnackbarKeys.ReceivedCumulativeUsageByLinkError);
    }
  }, [
    receivedTotalUsageByNetworkError,
    receivedUsageByNetworkError,
    receivedCumulativeUsageByNetworkError,

    receivedTotalUsageByLinkError,
    receivedUsageByLinkError,
    receivedCumulativeUsageByLinkError,

    isDataUsageLoading,
     
    onRetry,
    enqueueSnackbar,
    closeSnackbar,
  ]);

  return null;
};

export default DataUsageErrorFeedback;
